import { Button } from '@invent/wl-ui-kit-next/ui';
import { Plus } from 'lucide-react';
import * as React from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';

import { DataTable, FilterBar, SelectChip, StatusChip, type IDataColumn } from '@/design';
import { useAdminData } from '@/domains/admin';
import { formatDateTime } from '@/domains/survey';
import type { ContentPostKind, ContentPostStatus, IContentPost } from '../api/post-contracts';
import { postsApi } from '../api/posts-api';
import { postPath } from '../paths';
import { StatusPill, personName } from '../shared';
import { ContentPage, NEW_POST } from './content-page';

/**
 * News, insights and press releases — the list (WEB-06), over `/api/v1/admin/website/posts`.
 *
 * **One list for three kinds, filtered rather than split into three screens.** The requirement
 * names news, insights and press releases together and gives them one set of acceptance criteria;
 * they differ by a label and nothing else — same fields, same workflow, same URL shape. Three
 * screens would be three places to look for the thing you published yesterday.
 *
 * **The filters are the URL, and survey-api applies them.** Every Team Portal list works that way
 * (the two-portals design, §5), so a filtered view can be pasted to a colleague — which during a
 * launch week is most of what these screens are used for. The query string is handed to the
 * server rather than used to `.filter()` the answer, because a client-side filter is one that
 * silently filters a page.
 *
 * **"Write a post" creates the post, then opens it.** A round trip rather than an empty form,
 * because the address is the server's: it derives a slug from the title, refuses one another post
 * already holds, and freezes it at publication. A form that minted an id in the browser would be
 * guessing at all three, and the guess would only be found out at the first save.
 *
 * A post opens at its own address rather than in a pane beside the list, for the reason the
 * definition editor does (`zones/team/team-routes.tsx`): writing an article is somewhere an author
 * stays, and a link to one is worth pasting.
 */

const KIND_WORD: Record<ContentPostKind, string> = {
  news: 'News',
  insight: 'Insight',
  press_release: 'Press release'
};

export function ContentNewsPage() {
  const navigate = useNavigate();
  const [params, setParams] = useSearchParams();
  const kind = params.get('kind') ?? '';
  const status = params.get('status') ?? '';
  const [creating, setCreating] = React.useState(false);
  const [refusal, setRefusal] = React.useState<Nullable<string>>(null);

  const { data, error, reload } = useAdminData<IContentPost[]>(
    (signal) =>
      postsApi.list(
        {
          ...(kind ? { kind: kind as ContentPostKind } : {}),
          ...(status ? { status: status as ContentPostStatus } : {})
        },
        signal
      ),
    [kind, status]
  );

  const set = (key: string, value: string) => {
    const next = new URLSearchParams(params);
    if (value === '') next.delete(key);
    else next.set(key, value);
    setParams(next, { replace: true });
  };

  const create = async () => {
    setCreating(true);
    setRefusal(null);
    try {
      const { body } = await postsApi.create(NEW_POST());
      navigate(postPath(body.postId));
    } catch {
      // The placeholder address carries a token (`draftSlug`), so this is not a collision.
      setRefusal('The post was not created. Try again in a moment.');
      setCreating(false);
    }
  };

  const columns: IDataColumn<IContentPost>[] = [
    {
      id: 'title',
      header: 'Title',
      cell: (post) => (
        <div className="grid min-w-0 gap-0.5">
          <span className="truncate font-medium text-[var(--color-royal-blue)]">{post.title}</span>
          <span className="truncate text-xs text-muted-foreground">/news/{post.slug}</span>
        </div>
      )
    },
    {
      id: 'kind',
      header: 'Kind',
      cell: (post) => <StatusChip tone="neutral">{KIND_WORD[post.kind]}</StatusChip>
    },
    { id: 'status', header: 'Status', cell: (post) => <StatusPill status={post.status} /> },
    {
      id: 'author',
      header: 'By-line',
      cell: (post) =>
        post.author || <span className="text-muted-foreground">Not set</span>
    },
    {
      id: 'updated',
      header: 'Last change',
      cell: (post) => (
        <div className="grid gap-0.5">
          <span>{formatDateTime(post.updatedAt)}</span>
          <span className="text-xs text-muted-foreground">by {personName(post.updatedBy)}</span>
        </div>
      )
    }
  ];

  return (
    <ContentPage
      title="News & Insights"
      lede="Announcements, press releases and writing from the research team."
      actions={
        <Button size="sm" onClick={create} disabled={creating}>
          <Plus className="mr-1 h-4 w-4" aria-hidden />
          {creating ? 'Creating…' : 'Write a post'}
        </Button>
      }
    >
      {refusal && (
        <p className="text-sm text-[var(--color-deep-magenta)]" role="alert">
          {refusal}
        </p>
      )}

      <FilterBar
        count={data ? `${data.length} ${data.length === 1 ? 'post' : 'posts'}` : ''}
        onClear={
          kind || status ? () => setParams(new URLSearchParams(), { replace: true }) : undefined
        }
      >
        <SelectChip
          label="Kind"
          any="Any kind"
          value={kind || null}
          onChoose={(value) => set('kind', value ?? '')}
          options={[
            { value: 'news', label: 'News' },
            { value: 'insight', label: 'Insight' },
            { value: 'press_release', label: 'Press release' }
          ]}
        />
        <SelectChip
          label="Status"
          any="Any status"
          value={status || null}
          onChoose={(value) => set('status', value ?? '')}
          options={[
            { value: 'draft', label: 'Draft' },
            { value: 'published', label: 'Published' },
            { value: 'archived', label: 'Archived' }
          ]}
        />
      </FilterBar>

      <DataTable
        columns={columns}
        rows={data ?? []}
        rowKey={(post) => post.postId}
        onOpen={(post) => navigate(postPath(post.postId))}
        label="Posts"
        maxHeight="none"
        state={listState(data, error, reload, Boolean(kind || status))}
      />
    </ContentPage>
  );
}

/** What the table says when it has no rows to draw, which is three different things. */
function listState(
  data: Nullable<IContentPost[]>,
  error: Nullable<string>,
  reload: () => void,
  filtered: boolean
) {
  if (error) {
    return {
      kind: 'error' as const,
      content: (
        <div className="grid gap-2">
          <p>{error}</p>
          <div>
            <Button type="button" size="sm" variant="outline" onClick={reload}>
              Try again
            </Button>
          </div>
        </div>
      )
    };
  }
  if (!data) return { kind: 'loading' as const };
  return {
    kind: 'empty' as const,
    content: filtered
      ? 'No post matches those filters.'
      : 'Nothing written yet. "Write a post" starts one.'
  };
}
